import { apiCall } from "@/common/api/ApiResult.model";
import { ListResult } from "@/common/models";
import axios, { AxiosError, AxiosInstance, AxiosStatic } from "axios";
import {
  PdfReport,
  ReportTemplate,
  CreateReportTemplateInput,
  ReportTemplateSection,
  CmsReport,
  GenerateReportTemplateSection,
} from "./model";
import { cms } from "@/common/state/cms/cms.api";
import { delayBy, pick, singleton, uniq } from "@/fp";
import { SharedState } from "@/common/state";
import { DownloadService } from "@/common/download.service";
import { ToastType, UseToast } from "@/components/root/toast/toast.state";

const IS_LOCAL = false;
let c: AxiosInstance | AxiosStatic;

const BASE_URL = IS_LOCAL
  ? "http://localhost:5003/api" // /org/a8a30680-5b7e-4b7c-a812-2591e2f90bbc
  : "/pdf-report/api";
const client = singleton<void, AxiosInstance | AxiosStatic>(() => {
  if (IS_LOCAL) {
    c = axios.create({
      baseURL: BASE_URL,
    });
  } else {
    c = axios;
  }
  return c;
});

export const load_all_pdf_reports = apiCall<
  { cursor: string },
  ListResult<PdfReport>
>("reports.get_all", async ({ cursor }) => {
  const res = await client().get(BASE_URL + "/reports/user");
  return {
    success: true,
    value: {
      objects: res.data.map((r: PdfReport) => {
        r.runtime = {
          data_sources: uniq(r.sections.map((s) => s.data_source)),
        };
        return r;
      }),
      cursor: "",
    },
  };
});

export const create_report = apiCall<
  {
    name: string;
    sections: GenerateReportTemplateSection[];
    template_id: string;
  },
  {
    report: PdfReport;
    operation: "created" | "pending" | "in-progress" | "completed";
  }
>("reports.generate", async (input) => {
  console.log("[pdf-api] create_report payload:", JSON.stringify(input, null, 2));
  const res = await client().post(BASE_URL + "/reports", input);
  console.log("[pdf-api] create_report response:", res.data);
  return {
    success: true,
    value: res.data,
  };
});

export const get_report_status = apiCall<string[], PdfReport[]>(
  "reports.get_status",
  async (ids) => {
    console.log("get_report_status : ids :", ids);
    const ids_query = ids.map((id) => `ids=${id}`).join("&");
    console.log("ids_query:", ids_query);
    // ?${ids_query}
    const res = await client().get(BASE_URL + `/reports?${ids_query}`);
    return {
      success: true,
      value: res.data,
    };
  },
);

export const delete_report = apiCall<string, boolean>(
  "reports.delete",
  async (report_id) => {
    const res = await client().delete(BASE_URL + `/reports/${report_id}`);
    return {
      success: true,
      value: res.data,
    };
  },
);

export const download_report = async (
  report_id: string,
  report_name: string,
  format: "pdf" | "pptx" = "pdf",
) => {
  const is_pptx = format === "pptx";
  const query = is_pptx ? "?format=pptx" : "";
  let res;
  try {
    res = await client().get(
      BASE_URL + `/reports/storage/${report_id}${query}`,
    );
  } catch (e) {
    if (is_pptx && (e as AxiosError).response?.status === 404) {
      UseToast().show({
        type: ToastType.ALERT,
        msg: "A PowerPoint version of this report is not available",
        duration: 4000,
      });
      return;
    }
    throw e;
  }
  const public_url = res.data.url;
  const downloadFn = is_pptx
    ? DownloadService.from_web.pptx
    : DownloadService.from_web.pdf;
  const [, download] = downloadFn({
    authorization: SharedState.loggedUser.value.authorization,
    fileName: report_name + (is_pptx ? ".pptx" : ".pdf"),
    filePath: public_url,
  });
  await download();
  await delayBy(1000);
  // delete the public file
  await axios.post(`/pdf-report/api/reports/storage`, { url: public_url });
};

const get_report_images = cms<void, CmsReport[]>(
  (env) => `
  *[_type=="reports"]{
    ...,
    "small_img": small_img.asset->url,
    "a4_img": a4_img.asset->url,
  }
  `,
);

export const load_all_report_template_sections = apiCall<
  void,
  ReportTemplateSection[]
>("reports.get_all_template_sections", async () => {
  const res = await client().get(BASE_URL + "/templates/sections");
  const images = await get_report_images();
  if (images.success) {
    return {
      success: true,
      value: res.data.map((s: ReportTemplateSection) => ({
        ...s,
        displayed_question: s.question.replace(/\{[^}]*\}/g, "").trim(),
        runtime: images.value.find((img) => img.id === s.id),
      })),
    };
  } else {
    return {
      success: true,
      value: [],
    };
  }
});

export const load_all_report_templates = apiCall<
  { cursor: string },
  ListResult<ReportTemplate>
>("reports.get_all_templates", async ({ cursor }) => {
  const res = await client().get(BASE_URL + "/templates/user");
  let items = res.data.map((r: any) => {
    r.scheduled = r.scheduled || "none";
    r.mailing_list = r.mailing_list || [];
    r.access = r.access || "private";
    return r;
  });
  if (SharedState.is_biz_user.value) {
    items = items.filter((d: any) => d.access == "private");
  }
  return {
    success: true,
    value: {
      objects: items,
      cursor: "",
    },
  };
});

export const create_report_template = apiCall<
  CreateReportTemplateInput,
  ReportTemplate
>("reports.create_template", async (template) => {
  const res = await client().post(BASE_URL + "/templates", template);
  return {
    success: true,
    value: res.data,
  };
});

export const update_report_template = apiCall<ReportTemplate, boolean>(
  "reports.update_template",
  async (template) => {
    await client().put(
      BASE_URL + `/templates/${template.id}`,
      pick(
        "id",
        "name",
        "sections",
        "mailing_list",
        "scheduled",
        "access",
      )(template),
    );
    return {
      success: true,
      value: true,
    };
  },
);

export const delete_report_template = apiCall<string, ReportTemplate>(
  "reports.delete_template",
  async (template_id) => {
    const res = await client().delete(BASE_URL + `/templates/${template_id}`);
    return {
      success: true,
      value: res.data,
    };
  },
);
